/**
 * What a summed figure was added up from, opened from the figure itself.
 *
 * A year the materials print only by month or quarter is summed by the
 * server. Its link used to open the last part's cell alone, so FY2024
 * revenue of $8.3mm opened December's $3.2mm (Hackberry v5). The panel lists
 * every part with its own value, each opening the cell or page it was read
 * at, and "Open all" opens the sheet with every part highlighted.
 */

import React from "react";
import { createPortal } from "react-dom";

import { useFloatingPanel } from "../../hooks/useFloatingPanel";
import type {
  DealScreeningFact,
  DealScreeningPart,
  DealScreeningSource,
} from "../../types/dealScreening";
import {
  formatFactValue,
  formatSourceLabel,
  unconvertedCurrency,
} from "./dealScreeningFormat";

const PANEL_WIDTH = 320;

/** "Jan-2024A" → "Jan-2024"; "1Q2026P" → "1Q2026", with whether it is projected. */
export function partPeriod(part: DealScreeningPart): {
  name: string;
  projected: boolean;
} {
  const period = part.period ?? "";
  if (part.label) return { name: part.label, projected: false };
  const projected = /P$/.test(period);
  return { name: period.replace(/[AP]$/, ""), projected };
}

/** "12 months" / "4 quarters" / "2 lines": what the parts are. */
export function partsKind(parts: DealScreeningPart[]): string {
  const n = parts.length;
  const first = parts[0];
  if (first?.label) return `${n} line${n === 1 ? "" : "s"}`;
  if (/^\dQ/.test(first?.period ?? "")) return `${n} quarters`;
  return `${n} months`;
}

/** The stretch of cells the figure's link highlights, e.g. "Model!G66:R66". */
export function spanLabel(source: DealScreeningSource): string {
  const range = /[?&]range=([^&]+)/.exec(source.anchor)?.[1];
  if (range && source.sheet) {
    return `${source.filename} · ${source.sheet}!${decodeURIComponent(range)}`;
  }
  return source.page !== null ? `${source.filename} · p. ${source.page}` : source.filename;
}

export interface SummedPartsPopoverProps {
  /** The summed figure; `parts` and `source` are set. */
  fact: DealScreeningFact;
  /** The figure as the cell or the header block prints it. */
  shown: React.ReactNode;
  onOpenSource: (source: DealScreeningSource) => void;
}

const SummedPartsPopover: React.FC<SummedPartsPopoverProps> = ({
  fact,
  shown,
  onOpenSource,
}) => {
  const { open, setOpen, close, place, buttonRef, panelRef } =
    useFloatingPanel(PANEL_WIDTH);
  const parts = fact.parts ?? [];
  const currency = unconvertedCurrency(fact);
  const value = (v: number | null | undefined) =>
    formatFactValue(v ?? null, fact.unit, currency);
  const what = `${fact.label}${fact.period ? ` ${fact.period}` : ""}`;

  const openSource = (source: DealScreeningSource) => {
    setOpen(false);
    onOpenSource(source);
  };

  return (
    <span className="relative inline-block align-baseline">
      <button
        type="button"
        ref={buttonRef}
        data-testid={`summed-${fact.key}${fact.period ? `-${fact.period}` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`What ${what} was summed from`}
        onClick={() => (open ? close() : setOpen(true))}
        className="text-left underline decoration-1 decoration-[#004C4B]/50 underline-offset-2"
      >
        {shown}
      </button>
      {open &&
        place &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={`What ${what} was summed from`}
            data-testid={`summed-parts-${fact.key}`}
            style={{ top: place.top, left: place.left, width: PANEL_WIDTH }}
            className="fixed z-50 rounded-md border border-gray-200 bg-white p-2 text-left font-sans text-[12.5px] shadow-lg"
          >
            <p className="text-sm font-medium text-text-strong">
              {what}: sum of {partsKind(parts)}
            </p>
            {fact.source && (
              <button
                type="button"
                onClick={() => fact.source && openSource(fact.source)}
                data-testid="summed-open-all"
                className="mt-0.5 block text-left text-xs text-[#004C4B] hover:underline"
              >
                Open all · {spanLabel(fact.source)}
              </button>
            )}
            <ul className="mt-1.5 max-h-64 space-y-0.5 overflow-y-auto">
              {parts.map((part, index) => {
                const { name, projected } = partPeriod(part);
                const source = part.source ?? null;
                return (
                  <li
                    key={`${part.period ?? part.label ?? ""}-${index}`}
                    data-testid={`summed-part-${fact.key}-${index}`}
                  >
                    <button
                      type="button"
                      disabled={!source}
                      onClick={() => source && openSource(source)}
                      title={source ? `Open ${formatSourceLabel(source)}` : undefined}
                      className="flex w-full items-baseline justify-between gap-3 rounded px-1 text-sm text-text-muted enabled:hover:bg-gray-50 enabled:hover:text-text-base"
                    >
                      <span>
                        {name}
                        {projected && (
                          <span className="text-xs text-[#9CA3AF]"> projected</span>
                        )}
                        {source?.cell && (
                          <span className="text-xs text-[#9CA3AF]"> · {source.cell}</span>
                        )}
                      </span>
                      <span className="tabular-nums underline decoration-[#004C4B]/30 underline-offset-2">
                        {value(part.value)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-1 flex items-baseline justify-between gap-3 border-t border-[#f1f2f4] px-1 pt-1 text-sm font-medium text-text-strong">
              <span>Total</span>
              <span className="tabular-nums">
                {typeof fact.value === "number" ? value(fact.value) : null}
              </span>
            </p>
          </div>,
          document.body,
        )}
    </span>
  );
};

export default SummedPartsPopover;
